import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Paper,
  Stack,
  TextField,
  IconButton,
  CircularProgress,
  Container,
  Button,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import axios from "axios";
import { toast } from "react-toastify";
import AdminDashboard from "../../Admin/Dashboard/Dashboard";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { BASE_URL } from "../../Api/ApiUrls";
import { useNavigate } from "react-router-dom";

const AdPropertyTable = () => {
  const [properties, setProperties] = useState([]); 
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [search, setSearch] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Refs for aborting
  const requestControllerRef = useRef(null);
  const latestRequestId = useRef(0);

  const navigate = useNavigate();

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Core fetch function
  const fetchProperties = async (page, size, searchQuery) => {
    try {
      if (requestControllerRef.current) {
        requestControllerRef.current.abort();
      }
      requestControllerRef.current = new AbortController();
      const signal = requestControllerRef.current.signal;

      let url = `${BASE_URL}/ad-properties/combined/?page=${page}&page_size=${size}`;

      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }

      const res = await axios.get(url, { signal });

      const propertyData = res.data?.data || res.data || [];
      const pagination = res.data?.pagination || {};

      const transformedProperties = propertyData.map((property) => ({
        id: property.property_id,
        property_name: property.property_name || "N/A",
        property_type: property.property_type || "N/A",
        address: property.address || "N/A",
        city: property.city || "N/A",
        category_name: property.category_name || "N/A",
        user_first_name: property.user_first_name || "",
        user_last_name: property.user_last_name || "",
        user_mobile_no: property.user_mobile_no || "",
        user_email: property.user_email || "",
        created_at: property.created_at,
        originalProperty: property,
      }));

      return { data: transformedProperties, pagination };
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error("Error fetching properties:", error);
        toast.error("Failed to fetch properties");
      }
      return null;
    }
  };

  // Apply result to state
  const applyResult = (result, page) => {
    if (!result) return;
    const { data, pagination } = result;
    setProperties(data);
    setCurrentPage(pagination.current_page ?? page);
    setTotalPages(pagination.total_pages ?? 1);
    setTotalCount(pagination.total_count ?? data.length);
  };

  // Load page
  const loadPage = async (page, size, searchQuery) => {
    const requestId = ++latestRequestId.current;
    setIsLoading(true);
    const result = await fetchProperties(page, size, searchQuery);
    if (requestId !== latestRequestId.current) return;
    applyResult(result, page);
    setIsLoading(false);
    setHasLoadedOnce(true);
  };

  // Effects
  useEffect(() => {
    loadPage(1, pageSize, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      loadPage(1, pageSize, search);
    }, 300);
    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Handlers
  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  // Handle edit - navigate to edit page
  const handleEdit = (property) => {
    navigate(`/admin-ad-edit-property/${property.id}`);
  };

  const handleDelete = async (propertyId) => {
    if (window.confirm("Are you sure you want to delete this property?")) {
      try {
        await axios.delete(`${BASE_URL}/ad-properties/combined/${propertyId}/`);
        toast.success("Property deleted successfully");
        const nextPage =
          properties.length <= 1 && currentPage > 1
            ? currentPage - 1
            : currentPage;
        loadPage(nextPage, pageSize, search);
      } catch (error) {
        console.error("Failed to delete property:", error);
        toast.error("Failed to delete property");
      }
    }
  };

  const handlePageChange = (event, value) => {
    loadPage(value, pageSize, search);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
  };

  const handleExport = () => {
    if (properties.length === 0) {
      toast.warning("No data to export!");
      return;
    }

    const dataToExport = properties.map((property, index) => ({
      "S.No": index + 1,
      "Category": property.category_name,
      "First Name": property.user_first_name,
      "Last Name": property.user_last_name,
      "Mobile": property.user_mobile_no,
      "Email": property.user_email,
      "Property Name": property.property_name,
      "Property Type": property.property_type,
      "Address": property.address,
      "City": property.city,
      "Created At": formatDate(property.created_at),
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Properties");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
    saveAs(data, "Properties.xlsx");
  };

  return (
    <Container maxWidth={false} sx={{ mt: 4 }}>
      <AdminDashboard />

      <Box
        sx={{
          maxWidth: 1500,
          mx: "auto",
          p: 2,
          borderRadius: 2,
          boxShadow: 3,
          backgroundColor: "#fff",
        }}
      >
        <Typography
          variant="h4"
          sx={{ textAlign: "center", color: "primary.main", mb: 2 }}
        >
          Properties Table
        </Typography>

        {/* SEARCH + DROPDOWN */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 2,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <TextField
              label="Search by Name / Category / City"
              variant="outlined"
              value={search}
              onChange={handleSearchChange}
              size="small"
              sx={{ width: 300 }}
            />

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Records</InputLabel>
              <Select
                value={pageSize}
                label="Records"
                onChange={handlePageSizeChange}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
                <MenuItem value={40}>40</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Button variant="contained" color="success" onClick={handleExport}>
            Export
          </Button>
        </Box>

        {isLoading && properties.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ height: 300 }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    "S.No",
                    // "Category",
                     "Property Type",
                    "First Name",
                    "Last Name",
                    "Mobile",
                    "Email",
                    "Property Name",
                    "Address",
                    "City",
                    "Actions",
                  ].map((head) => (
                    <TableCell
                      key={head}
                      sx={{
                        backgroundColor: "#1976d2",
                        color: "white",
                        textAlign: "center",
                        fontWeight: "bold",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {properties.map((property, index) => (
                  <TableRow key={property.id}>
                    <TableCell align="center">
                      {(currentPage - 1) * pageSize + index + 1}
                    </TableCell>
                    {/* <TableCell align="center">
                      {property.category_name}
                    </TableCell> */}
                     <TableCell align="center">
                      {property.property_type}
                    </TableCell>
                    <TableCell align="center">
                      {property.user_first_name || "N/A"}
                    </TableCell>
                    <TableCell align="center">
                      {property.user_last_name || "N/A"}
                    </TableCell>
                    <TableCell align="center">
                      {property.user_mobile_no || "N/A"}
                    </TableCell>
                    <TableCell align="center">
                      {property.user_email || "N/A"}
                    </TableCell>
                    <TableCell align="center">
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                          }}
                        >
                          {property.property_name}
                        </Typography>
                        {property.created_at && (
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{ mt: 0.5, color: "text.secondary" }}
                          >
                            Created: {formatDate(property.created_at)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    {/* <TableCell align="center">
                      {property.property_type}
                    </TableCell> */}
                    <TableCell align="center">{property.address}</TableCell>
                    <TableCell align="center">{property.city}</TableCell>
                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="center"
                      >
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleEdit(property)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDelete(property.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* PAGINATION CONTROLS */}
        {hasLoadedOnce && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {properties.length} of {totalCount} records
            </Typography>

            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
                disabled={isLoading}
              />
            )}
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default AdPropertyTable; 